import { cva } from 'class-variance-authority';

/**
 * Underline applied to the children-text on Button/Transparent: 1px line, 1px below the
 * text, animates 0 → full width on hover/focus over `duration-fast` ease-in.
 *
 * Apply to a child <span> inside an element that has the `group` class.
 * Used by Button.tsx automatically; also applied manually inside NavigationPill where
 * NavLink-based links bypass the Button wrapper.
 */
export const TRANSPARENT_UNDERLINE_CLASSES =
  "relative leading-none after:absolute after:left-0 after:top-[calc(100%+2px)] after:h-px after:w-0 after:bg-current after:content-[''] after:transition-[width] after:duration-fast after:ease-in group-hover:after:w-full group-focus-visible:after:w-full";

/**
 * Design-system Button styling. Mirrors the figma component path:
 *   Button / Transparent / Large / Black           — nav-pill page links
 *   Button / Transparent / Small / Blue + iconRight — inline blue chevron CTAs
 *   Button / Transparent / Small / Black           — footer page links
 *   Button / Outlined    / Large / Blue            — pill outlined CTAs
 */
export const buttonVariants = cva(
  [
    'group inline-flex items-center justify-center gap-1 rounded-pill font-normal',
    'transition-colors duration-fast ease-smooth',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
    'disabled:pointer-events-none disabled:opacity-50',
  ],
  {
    variants: {
      variant: {
        // self-start: in any flex-col parent, the button sits at the left edge of its
        // column (cross-axis start) instead of stretching or inheriting align-items.
        transparent: 'self-start border-transparent',
        outlined: 'border',
      },
      size: {
        small: 'text-label-14',
        large: 'text-label-16',
      },
      tone: {
        black: '',
        blue: '',
        'blue-on-dark': '',
      },
    },
    compoundVariants: [
      // Padding by variant + size
      { variant: 'transparent', size: 'small', class: 'p-0' },
      { variant: 'transparent', size: 'large', class: 'px-2 py-1.5' },
      { variant: 'outlined', size: 'small', class: 'px-3 py-1.5' },
      { variant: 'outlined', size: 'large', class: 'px-4 py-2' },
      // Color — transparent (hover indication is the animated underline on the children <span>)
      {
        variant: 'transparent',
        tone: 'black',
        class: 'text-clearlab-midnight',
      },
      {
        variant: 'transparent',
        tone: 'blue',
        class: 'text-clearlab-blue',
      },
      {
        variant: 'transparent',
        tone: 'blue-on-dark',
        class: 'text-clearlab-blue-on-dark',
      },
      // Color — outlined. Hover fill is the same E2DFDA/40 as the nav border.
      {
        variant: 'outlined',
        tone: 'black',
        class: 'border-clearlab-midnight text-clearlab-midnight hover:bg-clearlab-divider-light/20',
      },
      {
        variant: 'outlined',
        tone: 'blue',
        class: 'border-clearlab-blue text-clearlab-blue hover:bg-clearlab-divider-light/20',
      },
      {
        variant: 'outlined',
        tone: 'blue-on-dark',
        class:
          'border-clearlab-blue-on-dark text-clearlab-blue-on-dark hover:bg-clearlab-divider-on-dark/20',
      },
    ],
    defaultVariants: {
      variant: 'transparent',
      size: 'large',
      tone: 'black',
    },
  },
);
